import {
    Autorenew,
    CheckCircle,
    ChevronRight,
    Description,
    DocumentScannerRounded,
    DrawRounded,
    Error,
    HelpRounded,
    MoreVert,
    Schedule,
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardActionArea,
    Chip,
    Container,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Pagination,
    Stack,
    Tab,
    TextField,
    Typography,
} from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';
import { useConfirm } from 'material-ui-confirm';
import { useHelper } from '../../../../context/HelpSidebarContext';
import useErrors from '../../../../hooks/useErrors';
import { useFetch } from '../../../../hooks/useFecth';
import server from '../../../../services/server';
import Dropdown from '../../../../components/Dropdown';
import { useAuth } from '../../../../context/AuthContext';
import { DialogSale } from './DialogSale';
import { ProposalHelpContent } from './ProposalHelpContent';

function tabSearchParams(tab, page = 1) {
    const next = { page };
    if (tab === 'generated') next.hasContract = 'true';
    if (tab === 'signed') next.isSigned = 'true';
    if (tab === 'paid') next.isPaid = 'true';
    if (tab === 'cancelled') next.isCancelled = 'true';
    return next;
}

export default function ProposalList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [input, setInput] = useState('');
    const [search, setSearch] = useState('');
    const { setErrors, enqueueSnackbar } = useErrors();
    const { chargeHelp } = useHelper();
    const confirm = useConfirm();
    const { user } = useAuth();
    const isSuper = Boolean(user?.super);

    // Lê todos os filtros da URL
    const page = parseInt(searchParams.get('page')) || 1;
    const hasContract = searchParams.get('hasContract') === 'true';
    const isSignedFilter = searchParams.get('isSigned') === 'true';
    const isPaid = searchParams.get('isPaid') === 'true';
    const isCancelled = searchParams.get('isCancelled') === 'true';

    // Determina qual tab está ativa baseado nos filtros
    const filterTab = isCancelled ? 'cancelled' : isPaid ? 'paid' : isSignedFilter ? 'signed' : hasContract ? 'generated' : 'all';

    // Debounce para busca
    const debouncedSearch = React.useMemo(
        () =>
            debounce(value => {
                setSearch(value);
                setSearchParams(tabSearchParams(filterTab, 1));
            }, 1000),
        [setSearchParams, filterTab],
    );

    // Cleanup do debounce
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    // Monta filtros para a requisição - usa os mesmos params da URL
    const filters = React.useMemo(() => {
        let f = {};
        if (search) f.search = search;
        f.page = page || 1;
        f.limit = 10;

        // Filtros vêm direto da URL
        if (hasContract) f.hasContract = true;
        if (isSignedFilter) f.isSigned = true;
        if (isPaid) f.isPaid = true;
        if (isCancelled) f.isCancelled = true;

        return f;
    }, [search, page, hasContract, isSignedFilter, isPaid, isCancelled]);

    const { data, mutate, error } = useFetch(`subscription`, {
        params: filters,
    });

    const statusSubscription = { ACTIVE: 'Ativa', OVERDUE: 'Vencida', CANCELED: 'Cancelada', WAITING: 'Aguardando' };

    const handleCancelContract = async (e, contractUuid) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            await confirm({
                title: 'Cancelar proposta',
                description: 'Tem certeza que deseja cancelar esta proposta? Ela irá para a aba de canceladas.',
                confirmationText: 'Cancelar proposta',
                cancellationText: 'Voltar',
            });
            await server.delete(`subscription/${contractUuid}`);
            await mutate();
            enqueueSnackbar('Proposta cancelada com sucesso.', { variant: 'success' });
        } catch (error) {
            if (error !== undefined) {
                setErrors(error);
            }
        }
    };

    const handleRestoreContract = async (e, contractUuid) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            await confirm({
                title: 'Restaurar proposta',
                description: 'Deseja restaurar esta proposta? Ela voltará para a lista ativa.',
                confirmationText: 'Restaurar',
                cancellationText: 'Voltar',
            });
            await server.put(`subscription/${contractUuid}/restore`);
            await mutate();
            enqueueSnackbar('Proposta restaurada com sucesso.', { variant: 'success' });
        } catch (error) {
            if (error !== undefined) {
                setErrors(error);
            }
        }
    };

    const handlePermanentDelete = async (e, contractUuid) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            await confirm({
                title: 'Excluir proposta permanentemente',
                description:
                    'Tem certeza de que deseja excluir esta proposta permanentemente? Pedidos, assinatura e arquivos da proposta serão apagados. Esta ação não pode ser desfeita.',
                confirmationText: 'Excluir permanentemente',
                cancellationText: 'Voltar',
            });
            await server.delete(`subscription/${contractUuid}`, { params: { permanent: true } });
            await mutate();
            enqueueSnackbar('Proposta excluída permanentemente.', { variant: 'success' });
        } catch (error) {
            if (error !== undefined) {
                setErrors(error);
            }
        }
    };


    return (
        <Container maxWidth="xl">
            <Stack pb={2} alignItems="center" justifyContent="space-between" direction="row">
                <Typography variant="h2">Propostas</Typography>
                <Button
                    variant="outlined"
                    startIcon={<HelpRounded />}
                    onClick={() => chargeHelp(<ProposalHelpContent />)}
                >
                    Ajuda
                </Button>
            </Stack>

            <Card>
                {/* Barra de ações: busca e criação de nova proposta */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" columnGap={2} p={2}>
                    {/* Campo de busca unificado: aceita nome (company/trade) ou documento sem máscara */}
                    <TextField
                        placeholder="Buscar por nome ou documento"
                        value={input}
                        onChange={e => {
                            setInput(e.target.value);
                            debouncedSearch(e.target.value);
                        }}
                        fullWidth
                        sx={{ maxWidth: 420 }}
                    />
                    <Button variant="outlined" component={Link} to="#contrato">
                        Criar nova proposta
                    </Button>
                </Stack>
                <Divider />

                {/* Tabs de Filtros */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                    <TabContext value={filterTab}>
                        <TabList
                            onChange={(e, newValue) => {
                                setSearchParams(tabSearchParams(newValue, 1));
                            }}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            <Tab
                                label={
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <span>Todos</span>
                                        {data?.total > 0 && <Chip label={data.total} size="small" color="default" />}
                                    </Stack>
                                }
                                value="all"
                            />
                            <Tab
                                label={
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <DocumentScannerRounded fontSize="small" />
                                        <span>Contrato Gerado</span>
                                    </Stack>
                                }
                                value="generated"
                            />
                            <Tab
                                label={
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <DrawRounded fontSize="small" />
                                        <span>Assinados</span>
                                    </Stack>
                                }
                                value="signed"
                            />
                            <Tab
                                label={
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <CheckCircle fontSize="small" />
                                        <span>Pagos</span>
                                    </Stack>
                                }
                                value="paid"
                            />
                            <Tab
                                label={
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Error fontSize="small" />
                                        <span>Canceladas</span>
                                    </Stack>
                                }
                                value="cancelled"
                            />
                        </TabList>
                    </TabContext>
                </Box>

                {/* Lista paginada das propostas filtradas */}
                <Box sx={{ p: 2 }}>
                    <Stack spacing={2}>
                        {data?.data?.map(item => {
                            const order = item?.Order?.find(i => i);
                            const hasCustomization = order?.Suborders && order.Suborders.length > 0;
                            const hasClub = Boolean(item?.Club?.id);
                            const hasUpdated = Boolean(item?.status === 'UPDATED');
                            const isContractGenerated = Boolean(item?.integrationId);
                            const isSigned = Boolean(item?.signedAt);
                            const itemIsPaid = ['CONFIRMED', 'RECEIVED'].includes(order?.paymentStatus);
                            const itemIsCancelled = Boolean(item?.deletedAt) || ['CANCELED', 'CANCELLED'].includes(item?.status);
                            const canCancel = !itemIsCancelled && (isSuper || (!hasClub && !isSigned && !itemIsPaid));

                            const contractStatus = item.status === 'CONCLUDED' ? 'Ativo' : item.status === 'CANCELED' || item.status === 'CANCELLED' ? 'Cancelado' : item.status === 'UPDATED' ? 'Contrato Atualizado' : itemIsCancelled ? 'Cancelado' : '';
                            const cardContentSx = { p: { xs: 2, sm: 2.5 } };
                            const Wrapper = itemIsCancelled ? Box : CardActionArea;
                            const wrapperProps = itemIsCancelled
                                ? { sx: cardContentSx }
                                : { component: Link, to: `/dashboard/propostas/${item.uuid}`, sx: cardContentSx };

                            return (
                                <Card key={item.uuid} variant="outlined">
                                    <Wrapper {...wrapperProps}>
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            spacing={{ xs: 2, sm: 3 }}
                                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                                        >
                                            {/* Ícone principal */}
                                            <Avatar
                                                sx={{
                                                    width: { xs: 48, sm: 56 },
                                                    height: { xs: 48, sm: 56 },
                                                    bgcolor: itemIsCancelled
                                                        ? 'error.main'
                                                        : hasUpdated
                                                          ? 'info.main'
                                                          : hasClub
                                                            ? 'success.main'
                                                            : 'warning.main',
                                                }}
                                            >
                                                {itemIsCancelled ? (
                                                    <Error sx={{ fontSize: 32 }} />
                                                ) : hasUpdated ? (
                                                    <Autorenew sx={{ fontSize: 32 }} />
                                                ) : hasClub ? (
                                                    <CheckCircle sx={{ fontSize: 32 }} />
                                                ) : (
                                                    <Schedule sx={{ fontSize: 32 }} />
                                                )}
                                            </Avatar>

                                            {/* Informações principais */}
                                            <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                                                {/* Título e Badge */}
                                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontSize: { xs: '1rem', sm: '1.1rem' },
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {item?.Club?.name ||
                                                            item?.contractData?.contract?.companyName ||
                                                            item?.contractData?.contract?.tradeName ||
                                                            item?.contractData?.contract?.contractorName ||
                                                            'Contrato ainda sem clube'}
                                                    </Typography>
                                                    <Chip label={`#${item.id}`} size="small" sx={{ fontWeight: 600 }} />
                                                    {itemIsCancelled && <Chip label="Cancelada" size="small" color="error" />}
                                                </Stack>

                                                {/* Informações em linha no desktop, coluna no mobile */}
                                                <Stack
                                                    direction={{ xs: 'column', sm: 'row' }}
                                                    spacing={{ xs: 1, sm: 3 }}
                                                    divider={
                                                        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                                                    }
                                                >
                                                    {/* Documento */}
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Description sx={{ fontSize: 18, color: 'action.active' }} />
                                                        <Typography variant="body2" color="text.secondary">
                                                            {item?.Club?.document ||
                                                                item?.contractData?.contract?.document ||
                                                                item.document ||
                                                                'Doc. não informado'}
                                                        </Typography>
                                                    </Stack>

                                                    {/* Status do Contrato Gerado */}
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        {isContractGenerated ? (
                                                            <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                                                        ) : (
                                                            <Schedule sx={{ fontSize: 18, color: 'warning.main' }} />
                                                        )}
                                                        <Typography variant="body2" color="text.secondary">
                                                            {isContractGenerated ? 'Contrato gerado' : 'Aguardando geração'}
                                                        </Typography>
                                                    </Stack>
                                                    {/* Status wesley de Contrato Atualizado */}
                                                    {Boolean(item.status) && item.status !== 'SIGNED' && (
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            {(item.status === 'CANCELLED' || item.status === 'CANCELED' || itemIsCancelled) && (
                                                                <Error sx={{ fontSize: 18, color: 'error.main' }} />
                                                            )}
                                                            {item.status === 'CONCLUDED' && !itemIsCancelled && (
                                                                <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                                                            )}
                                                            {hasUpdated && !itemIsCancelled && (
                                                                <Autorenew sx={{ fontSize: 18, color: 'info.main' }} />
                                                            )}
                                                            {contractStatus === 'Aberto' && (
                                                                <Schedule sx={{ fontSize: 18, color: 'warning.main' }} />
                                                            )}
                                                            <Typography variant="body2" color="text.secondary">
                                                                {contractStatus}
                                                            </Typography>
                                                        </Stack>
                                                    )}
                                                    {/* Status de Assinatura */}
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        {isSigned ? (
                                                            <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                                                        ) : (
                                                            <Schedule sx={{ fontSize: 18, color: 'warning.main' }} />
                                                        )}
                                                        <Typography variant="body2" color="text.secondary">
                                                            {isSigned ? 'Assinado' : 'Aguardando assinatura'}
                                                        </Typography>
                                                    </Stack>

                                                    {/* Customização */}
                                                    {hasCustomization && (
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <DrawRounded sx={{ fontSize: 18, color: 'info.main' }} />
                                                            <Typography variant="body2" color="info.main" fontWeight={500}>
                                                                Customizado
                                                            </Typography>
                                                        </Stack>
                                                    )}

                                                    {/* Status de Pagamento */}
                                                    {order && (
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            {order.paymentStatus === 'CONFIRMED' || order.paymentStatus === 'RECEIVED' ? (
                                                                <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                                                            ) : order.paymentStatus === 'PENDING' ? (
                                                                <Schedule sx={{ fontSize: 18, color: 'warning.main' }} />
                                                            ) : (
                                                                <Error sx={{ fontSize: 18, color: 'error.main' }} />
                                                            )}
                                                            <Typography variant="body2" color="text.secondary">
                                                                {order.paymentStatus === 'CONFIRMED' || order.paymentStatus === 'RECEIVED'
                                                                    ? 'Pago'
                                                                    : order.paymentStatus === 'PENDING'
                                                                        ? 'Pagamento pendente'
                                                                        : 'Não pago'}
                                                            </Typography>
                                                        </Stack>
                                                    )}
                                                </Stack>
                                            </Stack>

                                            <Stack direction="row" spacing={1} alignItems="center">
                                                {(canCancel || isSuper || itemIsCancelled) && (
                                                    <Box
                                                        onClick={e => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}
                                                    >
                                                        <Dropdown>
                                                            <IconButton size="small" aria-label="Mais ações">
                                                                <MoreVert />
                                                            </IconButton>
                                                            <Menu>
                                                                {itemIsCancelled && (
                                                                    <MenuItem onClick={e => handleRestoreContract(e, item.uuid)}>
                                                                        Restaurar proposta
                                                                    </MenuItem>
                                                                )}
                                                                {canCancel && (
                                                                    <MenuItem onClick={e => handleCancelContract(e, item.uuid)}>
                                                                        Cancelar proposta
                                                                    </MenuItem>
                                                                )}
                                                                {isSuper && (
                                                                    <MenuItem
                                                                        onClick={e => handlePermanentDelete(e, item.uuid)}
                                                                        sx={{ color: 'error.main' }}
                                                                    >
                                                                        Excluir permanentemente
                                                                    </MenuItem>
                                                                )}
                                                            </Menu>
                                                        </Dropdown>
                                                    </Box>
                                                )}
                                                {!itemIsCancelled && (
                                                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                                                        <ChevronRight sx={{ fontSize: 24, color: 'action.active' }} />
                                                    </Box>
                                                )}
                                            </Stack>
                                        </Stack>
                                    </Wrapper>
                                </Card>
                            );
                        })}
                    </Stack>

                    {/* Controles de paginação server-side */}
                    {data?.totalPages > 1 && (
                        <Stack direction="row" py={2} justifyContent="center">
                            <Pagination
                                page={page}
                                count={data?.totalPages}
                                onChange={(event, newPage) => {
                                    setSearchParams(tabSearchParams(filterTab, newPage));
                                }}
                                color="primary"
                            />
                        </Stack>
                    )}
                </Box>
            </Card>

            <DialogSale mutate={mutate} />
        </Container>
    );
}
